import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Link2, AlertTriangle, Check, BarChart3, Target,
  FileText, RefreshCw, Loader2, Zap, Code2, Undo2, Timer
} from 'lucide-react';
import { toast, Toaster } from '../ui/sonner';
import BulkInternalLinks from './BulkInternalLinks';
import BulkLinkInsertion from './BulkLinkInsertion';
import ScheduledInsertionPanel from './ScheduledInsertionPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InternalLinksDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [insertionMetrics, setInsertionMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [showInsertion, setShowInsertion] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, iRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/internal-links/dashboard/metrics`),
        fetch(`${BACKEND_URL}/api/internal-links/insert/metrics`),
      ]);
      if (mRes.ok) setMetrics(await mRes.json());
      if (iRes.ok) setInsertionMetrics(await iRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const m = metrics || {};
  const im = insertionMetrics || {};
  const clusters = Object.entries(m.cluster_coverage || {}).sort((a, b) => b[1] - a[1]);
  const maxCluster = clusters.length > 0 ? clusters[0][1] : 1;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="internal-links-dashboard">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Internal Linking Engine</h1>
              <p className="text-sm text-gray-500">SEO link health, coverage, and live insertion</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchAll} variant="outline" data-testid="refresh-metrics-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button onClick={() => setShowBulk(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-generate-btn">
              <Zap className="w-4 h-4 mr-2" /> Bulk Generate
            </Button>
            <Button onClick={() => setShowInsertion(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-insert-btn">
              <Code2 className="w-4 h-4 mr-2" /> Bulk Insert into HTML
            </Button>
            <Button onClick={() => setShowScheduler(true)} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="scheduler-btn">
              <Timer className="w-4 h-4 mr-2" /> Scheduled Insertion
            </Button>
          </div>
        </div>

        {/* Suggestion Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Avg Links/Post', value: m.avg_links_per_post || 0, icon: Link2, color: 'emerald',
              sub: `${m.total_approved_links || 0} total approved` },
            { label: 'Orphan Blogs', value: m.orphan_blogs || 0, icon: AlertTriangle,
              color: (m.orphan_blogs || 0) > 0 ? 'red' : 'green',
              sub: 'Posts with 0 links' },
            { label: 'Missing Service Links', value: m.blogs_missing_service_links || 0, icon: FileText,
              color: (m.blogs_missing_service_links || 0) > 0 ? 'amber' : 'green',
              sub: 'Posts without service links' },
            { label: 'Conversion Link %', value: `${m.conversion_link_pct || 0}%`, icon: Target,
              color: (m.conversion_link_pct || 0) >= 50 ? 'green' : 'amber',
              sub: `${m.blogs_with_conversion_links || 0} of ${m.total_published_blogs || 0} posts` },
          ].map((stat, i) => (
            <Card key={stat.label || `stat-${i}`} data-testid={`metric-${stat.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900" data-testid={`metric-value-${i}`}>{stat.value}</p>
                    <p className="text-xs font-medium text-gray-600">{stat.label}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400">{stat.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Insertion Health Metrics */}
        <Card className="mb-8" data-testid="insertion-metrics-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Code2 className="w-4 h-4 text-blue-600" /> HTML Insertion Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Links Inserted (Live)', value: im.total_inserted || 0, color: 'text-blue-600' },
                { label: 'Pending Insertion', value: im.pending_insertion || 0, color: im.pending_insertion > 0 ? 'text-amber-600' : 'text-green-600' },
                { label: 'Blogs with Live Links', value: im.blogs_with_live_links || 0, color: 'text-emerald-600' },
                { label: 'Insertion Success Rate', value: `${im.insertion_success_rate || 0}%`, color: 'text-blue-600' },
                { label: 'Rollbacks', value: im.rollback_count || 0, color: im.rollback_count > 0 ? 'text-amber-600' : 'text-gray-400' },
              ].map((s, i) => (
                <div key={m.label || `ins-metric-${i}`} className="text-center p-3 bg-gray-50 rounded-lg" data-testid={`ins-metric-${i}`}>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            {im.pending_insertion > 0 && (
              <div className="mt-4 flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {im.pending_insertion} approved links not yet inserted into HTML content
                </p>
                <Button size="sm" onClick={() => setShowInsertion(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="insert-pending-btn">
                  <Code2 className="w-3.5 h-3.5 mr-1" /> Insert Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* FDI Cluster Coverage */}
          <Card data-testid="fdi-coverage-card">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-600" /> FDI Cluster Link Coverage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">FDI Blogs Total</span>
                  <span className="font-bold" data-testid="fdi-total">{m.fdi_blogs_total || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">FDI Blogs with FDI Links</span>
                  <span className="font-bold text-green-600" data-testid="fdi-with-links">{m.fdi_blogs_with_fdi_links || 0}</span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${m.fdi_blogs_total ? Math.round((m.fdi_blogs_with_fdi_links / m.fdi_blogs_total) * 100) : 0}%` }} />
                </div>
                <p className="text-xs text-gray-400">
                  {m.fdi_blogs_total ? Math.round((m.fdi_blogs_with_fdi_links / m.fdi_blogs_total) * 100) : 0}% of FDI blogs have FDI cluster links
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Link Health Overview */}
          <Card data-testid="sufficient-links-card">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" /> Link Health Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Published Blogs</span>
                  <span className="font-bold">{m.total_published_blogs || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">With 2+ Internal Links</span>
                  <span className="font-bold text-green-600">{m.blogs_with_sufficient_links || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Orphan Blogs (0 links)</span>
                  <span className={`font-bold ${(m.orphan_blogs || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>{m.orphan_blogs || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Drafts with Suggestions</span>
                  <span className="font-bold text-blue-600">{m.drafts_with_suggestions || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Drafts with Approved Links</span>
                  <span className="font-bold text-emerald-600">{m.drafts_with_approved_links || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cluster Coverage */}
        <Card data-testid="cluster-coverage-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-teal-600" /> Cluster Coverage Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clusters.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No approved links yet. Generate and approve internal links to see cluster coverage.</p>
            ) : (
              <div className="space-y-3">
                {clusters.map(([cluster, count]) => (
                  <div key={cluster} className="flex items-center gap-3" data-testid={`cluster-${cluster}`}>
                    <span className="text-xs font-medium text-gray-600 w-28 truncate capitalize">{cluster.replace(/_/g, ' ')}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all flex items-center justify-end pr-2"
                        style={{ width: `${Math.max((count / maxCluster) * 100, 15)}%` }}>
                        <span className="text-[10px] text-white font-bold">{count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        {showBulk && <BulkInternalLinks onClose={() => { setShowBulk(false); fetchAll(); }} />}
        {showInsertion && <BulkLinkInsertion onClose={() => { setShowInsertion(false); fetchAll(); }} />}
        {showScheduler && <ScheduledInsertionPanel onClose={() => { setShowScheduler(false); fetchAll(); }} onRefresh={fetchAll} />}
      </div>
    </div>
  );
};

export default InternalLinksDashboard;
